import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Brain, 
  Clock, 
  Activity, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle,
  Syringe,
  Heart,
  Eye,
  TrendingUp,
  Zap,
  Shield,
  FileText,
  Target,
  Calculator,
  Sparkles,
  Loader2,
  Pill,
  Timer
} from 'lucide-react';

import { API_URL } from '../config';

export default function AVCExpertPage() {
  const [activeTab, setActiveTab] = useState('scores');
  
  // FAST Score
  const [fastScore, setFastScore] = useState({
    face: false,
    arms: false,
    speech: false,
    time: ''
  });
  
  // FAST-ED Score
  const [fastEdScore, setFastEdScore] = useState({
    face: false,
    arms: false,
    speech: false,
    eyes: false,
    dizziness: false,
    time: ''
  });
  
  // NIHSS Score (0-42 points)
  const [nihssScore, setNihssScore] = useState({
    conscience: 0,
    conscienceQuestions: 0,
    conscienceCommands: 0,
    gaze: 0,
    visual: 0,
    facialPalsy: 0,
    leftArm: 0,
    rightArm: 0,
    leftLeg: 0,
    rightLeg: 0,
    ataxia: 0,
    sensory: 0,
    language: 0,
    dysarthria: 0,
    extinction: 0
  });
  
  // Signes cliniques
  const [signesCliniques, setSignesCliniques] = useState([]);
  const signesOptions = [
    'Déficit moteur membre supérieur',
    'Déficit moteur membre inférieur',
    'Paralysie faciale',
    'Aphasie / Troubles du langage',
    'Dysarthrie',
    'Troubles de la déglutition',
    'Hémianopsie latérale homonyme',
    'Diplopie',
    'Vertiges rotatoires',
    'Ataxie / Incoordination',
    'Troubles sensitifs',
    'Céphalées intenses',
    'Confusion / Troubles de conscience',
    'Négligence spatiale'
  ];
  
  // AI Analysis
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  // Calculate scores
  const calculateFast = () => {
    return (fastScore.face ? 1 : 0) + (fastScore.arms ? 1 : 0) + (fastScore.speech ? 1 : 0);
  };

  const calculateFastEd = () => {
    return (fastEdScore.face ? 1 : 0) + 
           (fastEdScore.arms ? 1 : 0) + 
           (fastEdScore.speech ? 1 : 0) +
           (fastEdScore.eyes ? 1 : 0) +
           (fastEdScore.dizziness ? 1 : 0);
  };

  const calculateNihss = () => {
    return Object.values(nihssScore).reduce((sum, val) => sum + val, 0);
  };

  const getNihssInterpretation = (score) => {
    if (score <= 4) return { niveau: 'AVC mineur', color: 'text-green-400', urgence: 'Surveillance' };
    if (score <= 15) return { niveau: 'AVC modéré', color: 'text-yellow-400', urgence: 'Thrombolyse à discuter' };
    if (score <= 20) return { niveau: 'AVC sévère', color: 'text-orange-400', urgence: 'Thrombolyse/Thrombectomie' };
    return { niveau: 'AVC très sévère', color: 'text-red-400', urgence: 'Thrombectomie urgente' };
  };

  const toggleSigne = (signe) => {
    if (signesCliniques.includes(signe)) {
      setSignesCliniques(signesCliniques.filter(s => s !== signe));
    } else {
      setSignesCliniques([...signesCliniques, signe]);
    }
  };

  const handleAiAnalysis = async () => {
    const nihssTotal = calculateNihss();
    const fastTotal = calculateFast();
    const fastEdTotal = calculateFastEd();
    
    if (signesCliniques.length === 0) {
      toast.error('Veuillez sélectionner au moins un signe clinique');
      return;
    }

    setAiLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: 'avc_expert',
          audience: 'adult',
          prompt: `Analyse ce cas d'AVC suspecté:
- Score NIHSS: ${nihssTotal}/42 (${getNihssInterpretation(nihssTotal).niveau})
- Score FAST: ${fastTotal}/3
- Score FAST-ED: ${fastEdTotal}/5
- Signes cliniques: ${signesCliniques.join(', ')}
- Délai: ${fastScore.time || fastEdScore.time || 'Non renseigné'}

Fournis une analyse structurée avec:
1. Gravité et territoire vasculaire probable
2. Éligibilité thrombolyse/thrombectomie
3. Examens complémentaires prioritaires
4. Recommandations thérapeutiques
5. Points de vigilance`
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResult(data.response || data.analysis);
        toast.success('Analyse AI complétée');
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(`Erreur lors de l'analyse: ${errorData.detail || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setAiLoading(false);
    }
  };

  const fastInterpretation = calculateFast();
  const fastEdInterpretation = calculateFastEd();
  const nihssTotal = calculateNihss();
  const nihssInterp = getNihssInterpretation(nihssTotal);

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-br from-red-500/20 to-purple-500/20 rounded-xl border border-red-400/30">
            <Brain className="w-8 h-8 text-red-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[hsl(var(--text))]">AVC Expert</h1>
            <p className="text-[hsl(var(--text-dim))]">Évaluation complète et aide à la décision</p>
          </div>
        </div>
        
        <Alert className="bg-red-500/10 border-red-400/30">
          <Clock className="w-4 h-4 text-red-400" />
          <AlertDescription className="text-[hsl(var(--text))]">
            <strong>Le temps, c'est du cerveau :</strong> Chaque minute compte dans la prise en charge de l'AVC
          </AlertDescription>
        </Alert>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:grid-cols-9 mb-6">
          <TabsTrigger value="scores" data-testid="tab-scores">
            <Calculator className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Scores</span>
          </TabsTrigger>
          <TabsTrigger value="nihss" data-testid="tab-nihss">
            <Activity className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">NIHSS</span>
          </TabsTrigger>
          <TabsTrigger value="signes" data-testid="tab-signes">
            <Target className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Signes</span>
          </TabsTrigger>
          <TabsTrigger value="analyse" data-testid="tab-analyse">
            <Sparkles className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Analyse</span>
          </TabsTrigger>
          <TabsTrigger value="thrombolyse">
            <Syringe className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Thrombolyse</span>
          </TabsTrigger>
          <TabsTrigger value="thrombectomie">
            <Zap className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Thrombectomie</span>
          </TabsTrigger>
          <TabsTrigger value="controle-ta">
            <Heart className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Contrôle TA</span>
          </TabsTrigger>
          <TabsTrigger value="complications">
            <AlertTriangle className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Complications</span>
          </TabsTrigger>
          <TabsTrigger value="contre-indications">
            <XCircle className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">CI</span>
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: FAST & FAST-ED Scores */}
        <TabsContent value="scores" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* FAST Score Card */}
            <Card data-testid="fast-score-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-purple-400" />
                  Score FAST
                </CardTitle>
                <CardDescription>Face - Arms - Speech - Time</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <div>
                      <p className="font-medium text-[hsl(var(--text))]">Face (Asymétrie faciale)</p>
                      <p className="text-xs text-[hsl(var(--text-dim))]">Sourire, montrer les dents</p>
                    </div>
                    <Switch
                      checked={fastScore.face}
                      onCheckedChange={(val) => setFastScore({...fastScore, face: val})}
                      data-testid="fast-face"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <div>
                      <p className="font-medium text-[hsl(var(--text))]">Arms (Faiblesse bras)</p>
                      <p className="text-xs text-[hsl(var(--text-dim))]">Élévation 10 secondes</p>
                    </div>
                    <Switch
                      checked={fastScore.arms}
                      onCheckedChange={(val) => setFastScore({...fastScore, arms: val})}
                      data-testid="fast-arms"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <div>
                      <p className="font-medium text-[hsl(var(--text))]">Speech (Troubles parole)</p>
                      <p className="text-xs text-[hsl(var(--text-dim))]">Répéter phrase simple</p>
                    </div>
                    <Switch
                      checked={fastScore.speech}
                      onCheckedChange={(val) => setFastScore({...fastScore, speech: val})}
                      data-testid="fast-speech"
                    />
                  </div>

                  <div className="p-3 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <label className="block text-sm font-medium text-[hsl(var(--text))] mb-2">
                      Time (Heure de début)
                    </label>
                    <Input
                      type="time"
                      value={fastScore.time}
                      onChange={(e) => setFastScore({...fastScore, time: e.target.value})}
                      className="bg-[hsl(var(--bg))]"
                      data-testid="fast-time"
                    />
                  </div>
                </div>

                <div className="p-4 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-400/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[hsl(var(--text-dim))]">Score FAST</span>
                    <span className="text-2xl font-bold text-purple-400" data-testid="fast-result">
                      {fastInterpretation}/3
                    </span>
                  </div>
                  <Alert className={fastInterpretation > 0 ? "bg-red-500/10 border-red-400/30" : "bg-green-500/10 border-green-400/30"}>
                    <AlertTriangle className="w-4 h-4" />
                    <AlertDescription>
                      {fastInterpretation > 0 
                        ? `AVC probable - Appeler le 15 immédiatement`
                        : 'Aucun signe FAST détecté'}
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>

            {/* FAST-ED Score Card */}
            <Card data-testid="fast-ed-score-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-cyan-400" />
                  Score FAST-ED
                </CardTitle>
                <CardDescription>FAST + Eyes + Dizziness</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <div>
                      <p className="font-medium text-[hsl(var(--text))]">Face</p>
                      <p className="text-xs text-[hsl(var(--text-dim))]">Asymétrie faciale</p>
                    </div>
                    <Switch
                      checked={fastEdScore.face}
                      onCheckedChange={(val) => setFastEdScore({...fastEdScore, face: val})}
                      data-testid="fasted-face"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <div>
                      <p className="font-medium text-[hsl(var(--text))]">Arms</p>
                      <p className="text-xs text-[hsl(var(--text-dim))]">Faiblesse bras</p>
                    </div>
                    <Switch
                      checked={fastEdScore.arms}
                      onCheckedChange={(val) => setFastEdScore({...fastEdScore, arms: val})}
                      data-testid="fasted-arms"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <div>
                      <p className="font-medium text-[hsl(var(--text))]">Speech</p>
                      <p className="text-xs text-[hsl(var(--text-dim))]">Troubles parole</p>
                    </div>
                    <Switch
                      checked={fastEdScore.speech}
                      onCheckedChange={(val) => setFastEdScore({...fastEdScore, speech: val})}
                      data-testid="fasted-speech"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-[hsl(var(--bg-elev))] rounded-lg border border-cyan-400/30">
                    <div>
                      <p className="font-medium text-cyan-400">Eyes (Troubles visuels)</p>
                      <p className="text-xs text-[hsl(var(--text-dim))]">Diplopie, hémianopsie</p>
                    </div>
                    <Switch
                      checked={fastEdScore.eyes}
                      onCheckedChange={(val) => setFastEdScore({...fastEdScore, eyes: val})}
                      data-testid="fasted-eyes"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-[hsl(var(--bg-elev))] rounded-lg border border-cyan-400/30">
                    <div>
                      <p className="font-medium text-cyan-400">Dizziness (Vertiges)</p>
                      <p className="text-xs text-[hsl(var(--text-dim))]">Vertiges rotatoires</p>
                    </div>
                    <Switch
                      checked={fastEdScore.dizziness}
                      onCheckedChange={(val) => setFastEdScore({...fastEdScore, dizziness: val})}
                      data-testid="fasted-dizziness"
                    />
                  </div>

                  <div className="p-3 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <label className="block text-sm font-medium text-[hsl(var(--text))] mb-2">
                      Time (Heure de début)
                    </label>
                    <Input
                      type="time"
                      value={fastEdScore.time}
                      onChange={(e) => setFastEdScore({...fastEdScore, time: e.target.value})}
                      className="bg-[hsl(var(--bg))]"
                      data-testid="fasted-time"
                    />
                  </div>
                </div>

                <div className="p-4 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-400/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[hsl(var(--text-dim))]">Score FAST-ED</span>
                    <span className="text-2xl font-bold text-cyan-400" data-testid="fasted-result">
                      {fastEdInterpretation}/5
                    </span>
                  </div>
                  <Alert className={fastEdInterpretation > 0 ? "bg-red-500/10 border-red-400/30" : "bg-green-500/10 border-green-400/30"}>
                    <AlertTriangle className="w-4 h-4" />
                    <AlertDescription>
                      {fastEdInterpretation > 0 
                        ? `Score positif - AVC probable (territoire postérieur si E ou D+)`
                        : 'Aucun signe FAST-ED détecté'}
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TAB 2: NIHSS Scale - Keeping the full implementation from before */}
        <TabsContent value="nihss" className="space-y-6">
          <Card data-testid="nihss-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-orange-400" />
                Échelle NIHSS (National Institutes of Health Stroke Scale)
              </CardTitle>
              <CardDescription>
                Évaluation standardisée de la sévérité de l'AVC (0-42 points)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ScrollArea className="h-[600px] pr-4">
                <div className="space-y-4">
                  {/* 1a. Niveau de conscience */}
                  <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <p className="font-medium text-[hsl(var(--text))] mb-3">1a. Niveau de conscience</p>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { val: 0, label: 'Éveillé' },
                        { val: 1, label: 'Somnolent' },
                        { val: 2, label: 'Stuporeux' },
                        { val: 3, label: 'Coma' }
                      ].map(opt => (
                        <Button
                          key={opt.val}
                          variant={nihssScore.conscience === opt.val ? "default" : "outline"}
                          onClick={() => setNihssScore({...nihssScore, conscience: opt.val})}
                          className="text-xs"
                          data-testid={`nihss-conscience-${opt.val}`}
                        >
                          {opt.val}: {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* 1b. Questions */}
                  <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <p className="font-medium text-[hsl(var(--text))] mb-3">1b. Questions (mois, âge)</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { val: 0, label: '2 correctes' },
                        { val: 1, label: '1 correcte' },
                        { val: 2, label: '0 correcte' }
                      ].map(opt => (
                        <Button
                          key={opt.val}
                          variant={nihssScore.conscienceQuestions === opt.val ? "default" : "outline"}
                          onClick={() => setNihssScore({...nihssScore, conscienceQuestions: opt.val})}
                          className="text-xs"
                          data-testid={`nihss-questions-${opt.val}`}
                        >
                          {opt.val}: {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* 1c. Commandes */}
                  <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <p className="font-medium text-[hsl(var(--text))] mb-3">1c. Commandes (ouvrir/fermer yeux, serrer main)</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { val: 0, label: '2 correctes' },
                        { val: 1, label: '1 correcte' },
                        { val: 2, label: '0 correcte' }
                      ].map(opt => (
                        <Button
                          key={opt.val}
                          variant={nihssScore.conscienceCommands === opt.val ? "default" : "outline"}
                          onClick={() => setNihssScore({...nihssScore, conscienceCommands: opt.val})}
                          className="text-xs"
                          data-testid={`nihss-commands-${opt.val}`}
                        >
                          {opt.val}: {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* 2. Oculomotricité */}
                  <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <p className="font-medium text-[hsl(var(--text))] mb-3">2. Oculomotricité (mouvements oculaires)</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { val: 0, label: 'Normale' },
                        { val: 1, label: 'Parésie partielle' },
                        { val: 2, label: 'Déviation forcée' }
                      ].map(opt => (
                        <Button
                          key={opt.val}
                          variant={nihssScore.gaze === opt.val ? "default" : "outline"}
                          onClick={() => setNihssScore({...nihssScore, gaze: opt.val})}
                          className="text-xs"
                          data-testid={`nihss-gaze-${opt.val}`}
                        >
                          {opt.val}: {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* 3. Champ visuel */}
                  <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <p className="font-medium text-[hsl(var(--text))] mb-3">3. Champ visuel</p>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { val: 0, label: 'Normal' },
                        { val: 1, label: 'Quadranopsie' },
                        { val: 2, label: 'Hémianopsie' },
                        { val: 3, label: 'Cécité' }
                      ].map(opt => (
                        <Button
                          key={opt.val}
                          variant={nihssScore.visual === opt.val ? "default" : "outline"}
                          onClick={() => setNihssScore({...nihssScore, visual: opt.val})}
                          className="text-xs"
                          data-testid={`nihss-visual-${opt.val}`}
                        >
                          {opt.val}: {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* 4. Parésie faciale */}
                  <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <p className="font-medium text-[hsl(var(--text))] mb-3">4. Parésie faciale</p>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { val: 0, label: 'Normale' },
                        { val: 1, label: 'Mineure' },
                        { val: 2, label: 'Partielle' },
                        { val: 3, label: 'Complète' }
                      ].map(opt => (
                        <Button
                          key={opt.val}
                          variant={nihssScore.facialPalsy === opt.val ? "default" : "outline"}
                          onClick={() => setNihssScore({...nihssScore, facialPalsy: opt.val})}
                          className="text-xs"
                          data-testid={`nihss-facial-${opt.val}`}
                        >
                          {opt.val}: {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* 5. Motricité bras */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                      <p className="font-medium text-[hsl(var(--text))] mb-3">5a. Bras gauche</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { val: 0, label: '0: Pas de chute' },
                          { val: 1, label: '1: Chute <10s' },
                          { val: 2, label: '2: Chute avant 10s' },
                          { val: 3, label: '3: Aucun mouvement' },
                          { val: 4, label: '4: Pas de mouvement' }
                        ].map(opt => (
                          <Button
                            key={opt.val}
                            variant={nihssScore.leftArm === opt.val ? "default" : "outline"}
                            onClick={() => setNihssScore({...nihssScore, leftArm: opt.val})}
                            className="text-xs"
                            data-testid={`nihss-leftarm-${opt.val}`}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                      <p className="font-medium text-[hsl(var(--text))] mb-3">5b. Bras droit</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { val: 0, label: '0: Pas de chute' },
                          { val: 1, label: '1: Chute <10s' },
                          { val: 2, label: '2: Chute avant 10s' },
                          { val: 3, label: '3: Aucun mouvement' },
                          { val: 4, label: '4: Pas de mouvement' }
                        ].map(opt => (
                          <Button
                            key={opt.val}
                            variant={nihssScore.rightArm === opt.val ? "default" : "outline"}
                            onClick={() => setNihssScore({...nihssScore, rightArm: opt.val})}
                            className="text-xs"
                            data-testid={`nihss-rightarm-${opt.val}`}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* 6. Motricité jambes */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                      <p className="font-medium text-[hsl(var(--text))] mb-3">6a. Jambe gauche</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { val: 0, label: '0: Pas de chute' },
                          { val: 1, label: '1: Chute <5s' },
                          { val: 2, label: '2: Chute avant 5s' },
                          { val: 3, label: '3: Aucun mouvement' },
                          { val: 4, label: '4: Pas de mouvement' }
                        ].map(opt => (
                          <Button
                            key={opt.val}
                            variant={nihssScore.leftLeg === opt.val ? "default" : "outline"}
                            onClick={() => setNihssScore({...nihssScore, leftLeg: opt.val})}
                            className="text-xs"
                            data-testid={`nihss-leftleg-${opt.val}`}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                      <p className="font-medium text-[hsl(var(--text))] mb-3">6b. Jambe droite</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { val: 0, label: '0: Pas de chute' },
                          { val: 1, label: '1: Chute <5s' },
                          { val: 2, label: '2: Chute avant 5s' },
                          { val: 3, label: '3: Aucun mouvement' },
                          { val: 4, label: '4: Pas de mouvement' }
                        ].map(opt => (
                          <Button
                            key={opt.val}
                            variant={nihssScore.rightLeg === opt.val ? "default" : "outline"}
                            onClick={() => setNihssScore({...nihssScore, rightLeg: opt.val})}
                            className="text-xs"
                            data-testid={`nihss-rightleg-${opt.val}`}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* 7. Ataxie */}
                  <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <p className="font-medium text-[hsl(var(--text))] mb-3">7. Ataxie des membres</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { val: 0, label: 'Absente' },
                        { val: 1, label: '1 membre' },
                        { val: 2, label: '2 membres' }
                      ].map(opt => (
                        <Button
                          key={opt.val}
                          variant={nihssScore.ataxia === opt.val ? "default" : "outline"}
                          onClick={() => setNihssScore({...nihssScore, ataxia: opt.val})}
                          className="text-xs"
                          data-testid={`nihss-ataxia-${opt.val}`}
                        >
                          {opt.val}: {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* 8. Sensibilité */}
                  <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <p className="font-medium text-[hsl(var(--text))] mb-3">8. Sensibilité</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { val: 0, label: 'Normale' },
                        { val: 1, label: 'Hypoesthésie' },
                        { val: 2, label: 'Anesthésie' }
                      ].map(opt => (
                        <Button
                          key={opt.val}
                          variant={nihssScore.sensory === opt.val ? "default" : "outline"}
                          onClick={() => setNihssScore({...nihssScore, sensory: opt.val})}
                          className="text-xs"
                          data-testid={`nihss-sensory-${opt.val}`}
                        >
                          {opt.val}: {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* 9. Langage */}
                  <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <p className="font-medium text-[hsl(var(--text))] mb-3">9. Langage (aphasie)</p>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { val: 0, label: 'Normal' },
                        { val: 1, label: 'Légère' },
                        { val: 2, label: 'Sévère' },
                        { val: 3, label: 'Mutisme' }
                      ].map(opt => (
                        <Button
                          key={opt.val}
                          variant={nihssScore.language === opt.val ? "default" : "outline"}
                          onClick={() => setNihssScore({...nihssScore, language: opt.val})}
                          className="text-xs"
                          data-testid={`nihss-language-${opt.val}`}
                        >
                          {opt.val}: {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* 10. Dysarthrie */}
                  <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <p className="font-medium text-[hsl(var(--text))] mb-3">10. Dysarthrie</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { val: 0, label: 'Normale' },
                        { val: 1, label: 'Légère/Modérée' },
                        { val: 2, label: 'Sévère' }
                      ].map(opt => (
                        <Button
                          key={opt.val}
                          variant={nihssScore.dysarthria === opt.val ? "default" : "outline"}
                          onClick={() => setNihssScore({...nihssScore, dysarthria: opt.val})}
                          className="text-xs"
                          data-testid={`nihss-dysarthria-${opt.val}`}
                        >
                          {opt.val}: {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* 11. Extinction/Négligence */}
                  <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <p className="font-medium text-[hsl(var(--text))] mb-3">11. Extinction et négligence</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { val: 0, label: 'Absente' },
                        { val: 1, label: 'Partielle' },
                        { val: 2, label: 'Complète' }
                      ].map(opt => (
                        <Button
                          key={opt.val}
                          variant={nihssScore.extinction === opt.val ? "default" : "outline"}
                          onClick={() => setNihssScore({...nihssScore, extinction: opt.val})}
                          className="text-xs"
                          data-testid={`nihss-extinction-${opt.val}`}
                        >
                          {opt.val}: {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </ScrollArea>

              {/* NIHSS Total Result */}
              <div className="mt-6 p-6 bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-400/30 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-lg font-medium text-[hsl(var(--text-dim))]">Score NIHSS Total</span>
                  <span className={`text-4xl font-bold ${nihssInterp.color}`} data-testid="nihss-total">
                    {nihssTotal}/42
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[hsl(var(--text-dim))]">Sévérité</span>
                    <span className={`font-semibold ${nihssInterp.color}`}>{nihssInterp.niveau}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[hsl(var(--text-dim))]">Recommandation</span>
                    <span className="font-semibold text-[hsl(var(--text))]">{nihssInterp.urgence}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 3: Signes Cliniques - Keeping implementation */}
        <TabsContent value="signes" className="space-y-6">
          <Card data-testid="signes-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-green-400" />
                Sélection des signes cliniques
              </CardTitle>
              <CardDescription>
                Cochez tous les signes cliniques présents chez le patient
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-3">
                {signesOptions.map((signe, idx) => (
                  <div
                    key={idx}
                    className={`p-4 rounded-lg border transition-all cursor-pointer ${
                      signesCliniques.includes(signe)
                        ? 'bg-green-500/20 border-green-400/50'
                        : 'bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))] hover:border-green-400/30'
                    }`}
                    onClick={() => toggleSigne(signe)}
                    data-testid={`signe-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-[hsl(var(--text))]">{signe}</span>
                      {signesCliniques.includes(signe) ? (
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                      ) : (
                        <div className="w-5 h-5 rounded border-2 border-[hsl(var(--border))]"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[hsl(var(--text-dim))]">
                    Signes sélectionnés
                  </span>
                  <Badge variant="outline" className="text-lg" data-testid="signes-count">
                    {signesCliniques.length}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 4: AI Analysis - Keeping implementation */}
        <TabsContent value="analyse" className="space-y-6">
          <Card data-testid="analyse-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-400" />
                Analyse AI du cas
              </CardTitle>
              <CardDescription>
                Analyse intelligente basée sur les scores et signes cliniques
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-400/30 rounded-lg">
                  <p className="text-xs text-[hsl(var(--text-dim))] mb-1">FAST</p>
                  <p className="text-2xl font-bold text-purple-400">{calculateFast()}/3</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-400/30 rounded-lg">
                  <p className="text-xs text-[hsl(var(--text-dim))] mb-1">FAST-ED</p>
                  <p className="text-2xl font-bold text-cyan-400">{calculateFastEd()}/5</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-400/30 rounded-lg">
                  <p className="text-xs text-[hsl(var(--text-dim))] mb-1">NIHSS</p>
                  <p className="text-2xl font-bold text-orange-400">{nihssTotal}/42</p>
                </div>
              </div>

              <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                <p className="text-sm font-medium text-[hsl(var(--text-dim))] mb-2">
                  Signes cliniques présents
                </p>
                <div className="flex flex-wrap gap-2">
                  {signesCliniques.length > 0 ? (
                    signesCliniques.map((signe, idx) => (
                      <Badge key={idx} variant="outline" className="bg-green-500/10 border-green-400/30">
                        {signe}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-sm text-[hsl(var(--text-dim))]">Aucun signe sélectionné</p>
                  )}
                </div>
              </div>

              <Button
                onClick={handleAiAnalysis}
                disabled={aiLoading || signesCliniques.length === 0}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                data-testid="analyse-ai-button"
              >
                {aiLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Lancer l'analyse AI
                  </>
                )}
              </Button>

              {aiResult && (
                <div className="p-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-400/30 rounded-lg">
                  <h3 className="text-lg font-semibold text-purple-400 mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Résultat de l'analyse
                  </h3>
                  <div className="prose prose-invert max-w-none">
                    <div className="text-[hsl(var(--text))] whitespace-pre-wrap" data-testid="ai-result">
                      {aiResult}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 5: Thrombolyse - NEW: Adding content from old version */}
        <TabsContent value="thrombolyse" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Syringe className="w-5 h-5 text-blue-400" />
                Thrombolyse IV
              </CardTitle>
              <CardDescription>
                Fenêtres thérapeutiques et protocoles d'administration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Fenêtres thérapeutiques */}
              <div>
                <h3 className="text-lg font-semibold text-[hsl(var(--text))] mb-4">Fenêtres thérapeutiques</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-400/30 rounded-lg">
                    <p className="text-sm font-medium text-blue-400 mb-2">Standard</p>
                    <p className="text-2xl font-bold text-[hsl(var(--text))]">0 à 4,5h</p>
                    <p className="text-xs text-[hsl(var(--text-dim))] mt-2">Fenêtre classique</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-400/30 rounded-lg">
                    <p className="text-sm font-medium text-purple-400 mb-2">Étendue</p>
                    <p className="text-2xl font-bold text-[hsl(var(--text))]">Jusqu'à 9h</p>
                    <p className="text-xs text-[hsl(var(--text-dim))] mt-2">Sélection par imagerie (WAKE-UP, EXTEND)</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-400/30 rounded-lg">
                    <p className="text-sm font-medium text-orange-400 mb-2">Très étendue</p>
                    <p className="text-2xl font-bold text-[hsl(var(--text))]">Jusqu'à 24h</p>
                    <p className="text-xs text-[hsl(var(--text-dim))] mt-2">Avec thrombectomie + imagerie (DEFUSE-3, DAWN)</p>
                  </div>
                </div>
              </div>

              {/* Agents thrombolytiques */}
              <div>
                <h3 className="text-lg font-semibold text-[hsl(var(--text))] mb-4">Agents thrombolytiques</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-[hsl(var(--text))]">Alteplase (rtPA)</h4>
                      <Badge variant="outline">Standard</Badge>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />
                        <div>
                          <p className="font-medium text-[hsl(var(--text))]">Posologie:</p>
                          <p className="text-[hsl(var(--text-dim))]">0,9 mg/kg (maximum 90 mg)</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />
                        <div>
                          <p className="font-medium text-[hsl(var(--text))]">Administration:</p>
                          <p className="text-[hsl(var(--text-dim))]">10% en bolus IV, 90% en perfusion sur 60 minutes</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-green-400/30">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-[hsl(var(--text))]">Ténectéplase (TNK)</h4>
                      <Badge className="bg-green-500/20 text-green-400 border-green-400/30">NOUVEAU STANDARD 2025</Badge>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />
                        <div>
                          <p className="font-medium text-[hsl(var(--text))]">Posologie:</p>
                          <p className="text-[hsl(var(--text-dim))]">0,25 mg/kg (dose unique)</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />
                        <div>
                          <p className="font-medium text-[hsl(var(--text))]">Administration:</p>
                          <p className="text-[hsl(var(--text-dim))]">Bolus unique 5 secondes</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Indications */}
              <div>
                <h3 className="text-lg font-semibold text-[hsl(var(--text))] mb-4">Indications</h3>
                <div className="space-y-2">
                  {[
                    'AVC ischémique aigu avec déficit neurologique',
                    'Imagerie éliminant une hémorragie intracérébrale',
                    'Délai < 4,5 heures depuis le début des symptômes',
                    'NIHSS > 4 (ou < 4 si symptômes invalidants)',
                    'Plaquettes ≥ 100 000/mm³',
                    'INR ≤ 1,7 (si sous AVK)',
                    'PAS < 185 mmHg et PAD < 110 mmHg'
                  ].map((indication, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-green-500/10 rounded-lg border border-green-400/30">
                      <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[hsl(var(--text))]">{indication}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 6: Thrombectomie */}
        <TabsContent value="thrombectomie" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                Thrombectomie Mécanique
              </CardTitle>
              <CardDescription>
                Critères d'éligibilité et délais cibles
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Délais cibles */}
              <div>
                <h3 className="text-lg font-semibold text-[hsl(var(--text))] mb-4">Délais cibles</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-400/30 rounded-lg">
                    <p className="text-sm font-medium text-yellow-400 mb-2">Door-to-Groin</p>
                    <p className="text-2xl font-bold text-[hsl(var(--text))]">&lt; 75 min</p>
                    <p className="text-xs text-[hsl(var(--text-dim))] mt-2">(objectif &lt; 90 min)</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-400/30 rounded-lg">
                    <p className="text-sm font-medium text-blue-400 mb-2">Classique</p>
                    <p className="text-2xl font-bold text-[hsl(var(--text))]">&lt; 6 heures</p>
                    <p className="text-xs text-[hsl(var(--text-dim))] mt-2">Fenêtre standard</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-400/30 rounded-lg">
                    <p className="text-sm font-medium text-purple-400 mb-2">Étendue</p>
                    <p className="text-2xl font-bold text-[hsl(var(--text))]">6 à 24h</p>
                    <p className="text-xs text-[hsl(var(--text-dim))] mt-2">Avec sélection par imagerie</p>
                  </div>
                </div>
              </div>

              {/* Indications */}
              <div>
                <h3 className="text-lg font-semibold text-[hsl(var(--text))] mb-4">Indications</h3>
                <div className="space-y-2">
                  {[
                    'Occlusion artérielle proximale symptomatique (ICA, M1, M2 proximal)',
                    'Déficit neurologique significatif (NIHSS ≥ 6)',
                    'Bon état fonctionnel pré-AVC (mRS ≤ 1)',
                    'Imagerie avec mismatch significatif (DEFUSE-3) ou critères clinico-radiologiques (DAWN)'
                  ].map((indication, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-yellow-500/10 rounded-lg border border-yellow-400/30">
                      <CheckCircle2 className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[hsl(var(--text))]">{indication}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Critères DAWN */}
              <div>
                <h3 className="text-lg font-semibold text-[hsl(var(--text))] mb-4">Critères DAWN (6-24h)</h3>
                <div className="space-y-3">
                  {[
                    { age: '≥ 80 ans', nihss: 'NIHSS ≥ 10', volume: 'Volume < 21 mL' },
                    { age: '65-79 ans', nihss: 'NIHSS ≥ 10', volume: 'Volume < 31 mL' },
                    { age: '18-64 ans', nihss: 'NIHSS ≥ 10', volume: 'Volume < 51 mL' }
                  ].map((critere, idx) => (
                    <div key={idx} className="p-4 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-[hsl(var(--text-dim))] mb-1">Âge</p>
                          <p className="font-semibold text-[hsl(var(--text))]">{critere.age}</p>
                        </div>
                        <div>
                          <p className="text-[hsl(var(--text-dim))] mb-1">Score</p>
                          <p className="font-semibold text-[hsl(var(--text))]">{critere.nihss}</p>
                        </div>
                        <div>
                          <p className="text-[hsl(var(--text-dim))] mb-1">Infarctus</p>
                          <p className="font-semibold text-[hsl(var(--text))]">{critere.volume}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Critères DEFUSE-3 */}
              <div>
                <h3 className="text-lg font-semibold text-[hsl(var(--text))] mb-4">Critères DEFUSE-3 (6-16h)</h3>
                <div className="space-y-2">
                  {[
                    'Ratio mismatch perfusion ≥ 1,8',
                    'Volume mismatch ≥ 15 mL',
                    'Volume infarctus < 70 mL'
                  ].map((critere, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-purple-500/10 rounded-lg border border-purple-400/30">
                      <CheckCircle2 className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[hsl(var(--text))]">{critere}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 7: Contrôle TA */}
        <TabsContent value="controle-ta" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-400" />
                Contrôle de la Tension Artérielle
              </CardTitle>
              <CardDescription>
                Objectifs tensionnels selon le contexte
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Objectifs tensionnels */}
              <div>
                <h3 className="text-lg font-semibold text-[hsl(var(--text))] mb-4">Objectifs selon le contexte</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-400/30 rounded-lg">
                    <h4 className="font-semibold text-blue-400 mb-2">AVC Ischémique (avant thrombolyse)</h4>
                    <p className="text-2xl font-bold text-[hsl(var(--text))]">PAS &lt; 185 mmHg</p>
                    <p className="text-xl font-bold text-[hsl(var(--text))]">PAD &lt; 110 mmHg</p>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-400/30 rounded-lg">
                    <h4 className="font-semibold text-purple-400 mb-2">AVC Ischémique (après thrombolyse)</h4>
                    <p className="text-2xl font-bold text-[hsl(var(--text))]">PAS &lt; 160 mmHg</p>
                    <p className="text-xl font-bold text-[hsl(var(--text))]">PAD &lt; 105 mmHg</p>
                    <Badge variant="outline" className="mt-2">Recommandé: 140-160 / 90-100 mmHg</Badge>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-400/30 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-red-400">AVC Hémorragique (HIC)</h4>
                      <Badge variant="outline" className="text-red-400 border-red-400/50">⚠️ Dans les 6 premières heures</Badge>
                    </div>
                    <p className="text-2xl font-bold text-[hsl(var(--text))]">PAS &lt; 140 mmHg</p>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-orange-500/10 to-yellow-500/10 border border-orange-400/30 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-orange-400">Hémorragie sous-arachnoïdienne (HSA)</h4>
                      <Badge variant="outline" className="text-orange-400 border-orange-400/50">Individualisé</Badge>
                    </div>
                    <p className="text-2xl font-bold text-[hsl(var(--text))]">PAS &lt; 140 mmHg</p>
                  </div>
                </div>
              </div>

              {/* Médicaments recommandés */}
              <div>
                <h3 className="text-lg font-semibold text-[hsl(var(--text))] mb-4">Médicaments recommandés</h3>
                <div className="space-y-3">
                  {[
                    { nom: 'Nicardipine', admin: 'Bolus et perfusion' },
                    { nom: 'Labétalol', admin: 'Bolus et perfusion' },
                    { nom: 'Urapidil', admin: 'Bolus et perfusion' },
                    { nom: 'Nitroglycérine', admin: 'Perfusion (contrôle PAD)' }
                  ].map((med, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                      <div className="flex items-center gap-3">
                        <Pill className="w-5 h-5 text-cyan-400" />
                        <span className="font-medium text-[hsl(var(--text))]">{med.nom}</span>
                      </div>
                      <Badge variant="outline">{med.admin}</Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Principes */}
              <div>
                <h3 className="text-lg font-semibold text-[hsl(var(--text))] mb-4">Principes de baisse tensionnelle</h3>
                <div className="space-y-2">
                  {[
                    'Titration progressive pour éviter une chute brutale',
                    'Surveillance continue ou toutes les 15 minutes',
                    'Voie intraveineuse privilégiée',
                    'Ajustement selon la réponse et les objectifs'
                  ].map((principe, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-cyan-500/10 rounded-lg border border-cyan-400/30">
                      <CheckCircle2 className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[hsl(var(--text))]">{principe}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 8: Complications */}
        <TabsContent value="complications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
                Complications de l'AVC
              </CardTitle>
              <CardDescription>
                Surveillance et prise en charge des complications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Accordion type="single" collapsible className="w-full">
                {/* Transformation hémorragique */}
                <AccordionItem value="hemorragique">
                  <AccordionTrigger className="text-[hsl(var(--text))]">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                      <span>Transformation Hémorragique</span>
                      <Badge variant="outline" className="ml-2">15-25% asymptomatique</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-4">
                      <div>
                        <p className="text-sm font-medium text-[hsl(var(--text-dim))] mb-2">Facteurs de risque</p>
                        <div className="flex flex-wrap gap-2">
                          {['Âge > 80 ans', 'NIHSS élevé', 'Diabète', 'Fibrillation atriale'].map((f, i) => (
                            <Badge key={i} variant="outline" className="bg-red-500/10 border-red-400/30">{f}</Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[hsl(var(--text-dim))] mb-2">Surveillance</p>
                        <div className="space-y-2">
                          <div className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />
                            <span className="text-[hsl(var(--text))]">Scanner de contrôle à 24h systématique après thrombolyse</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[hsl(var(--text-dim))] mb-2">Gestion</p>
                        <div className="space-y-2">
                          {['Arrêt antithrombotiques', 'Contrôle TA strict', 'Renversement anticoagulation', 'Consultation neurochirurgie si besoin'].map((g, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm">
                              <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />
                              <span className="text-[hsl(var(--text))]">{g}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Œdème cérébral */}
                <AccordionItem value="oedeme">
                  <AccordionTrigger className="text-[hsl(var(--text))]">
                    <div className="flex items-center gap-2">
                      <Brain className="w-5 h-5 text-purple-400" />
                      <span>Œdème cérébral</span>
                      <Badge variant="outline" className="ml-2">10% infarctus étendus</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-4">
                      <div>
                        <p className="text-sm font-medium text-[hsl(var(--text-dim))] mb-2">Présentation clinique</p>
                        <p className="text-sm text-[hsl(var(--text))]">Dégradation neurologique J2-J5, effet de masse</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[hsl(var(--text-dim))] mb-2">Facteurs de risque</p>
                        <div className="flex flex-wrap gap-2">
                          {['Âge jeune', 'Infarctus >50% territoire sylvien', 'Transformation hémorragique'].map((f, i) => (
                            <Badge key={i} variant="outline" className="bg-purple-500/10 border-purple-400/30">{f}</Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[hsl(var(--text-dim))] mb-2">Traitement</p>
                        <div className="space-y-2">
                          {['Osmothérapie (Mannitol, SSH)', 'Tête surélevée 30°', 'Normothermie', 'Normocapnie'].map((t, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm">
                              <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />
                              <span className="text-[hsl(var(--text))]">{t}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[hsl(var(--text-dim))] mb-2">Chirurgie</p>
                        <p className="text-sm text-[hsl(var(--text))]">Craniectomie décompressive si critères spécifiques</p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Crises épileptiques */}
                <AccordionItem value="epilepsie">
                  <AccordionTrigger className="text-[hsl(var(--text))]">
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-yellow-400" />
                      <span>Crises épileptiques</span>
                      <Badge variant="outline" className="ml-2">5-10% précoces</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-4">
                      <div>
                        <p className="text-sm font-medium text-[hsl(var(--text-dim))] mb-2">Fréquence</p>
                        <div className="space-y-2">
                          <p className="text-sm text-[hsl(var(--text))]">• Précoces: 5-10% (J1-J7)</p>
                          <p className="text-sm text-[hsl(var(--text))]">• Tardives: 10-15% (>J7)</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[hsl(var(--text-dim))] mb-2">Traitement</p>
                        <div className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />
                          <span className="text-[hsl(var(--text))]">Antiépileptiques (lévétiracétam)</span>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Pneumopathie d'inhalation */}
                <AccordionItem value="pneumopathie">
                  <AccordionTrigger className="text-[hsl(var(--text))]">
                    <div className="flex items-center gap-2">
                      <Activity className="w-5 h-5 text-cyan-400" />
                      <span>Pneumopathie d'inhalation</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-4">
                      <div>
                        <p className="text-sm font-medium text-[hsl(var(--text-dim))] mb-2">Prévention</p>
                        <div className="space-y-2">
                          {['Dépistage dysphagie', 'Position tête surélevée', 'Textures adaptées'].map((p, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm">
                              <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />
                              <span className="text-[hsl(var(--text))]">{p}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 9: Contre-indications */}
        <TabsContent value="contre-indications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-400" />
                Contre-indications à la thrombolyse
              </CardTitle>
              <CardDescription>
                Contre-indications absolues et relatives
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="absolues">
                  <AccordionTrigger className="text-[hsl(var(--text))]">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-5 h-5 text-red-400" />
                      <span>Contre-indications absolues</span>
                      <Badge variant="outline" className="ml-2 bg-red-500/20 border-red-400/50">11 critères</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pt-4">
                      {[
                        'Hémorragie intracrânienne à l\'imagerie',
                        'AVC ou trauma crânien grave < 3 mois',
                        'Chirurgie intracrânienne ou rachidienne < 3 mois',
                        'Hémorragie gastro-intestinale ou urinaire < 21 jours',
                        'Ponction artérielle non compressible < 7 jours',
                        'Hémorragie active ou diathèse hémorragique',
                        'Plaquettes < 100 000/mm³',
                        'INR > 1,7 (si sous AVK)',
                        'Héparine dans les 48h avec TCA élevé',
                        'Traitement par anticoagulant oral direct dans les 48h',
                        'Glycémie < 2,8 mmol/L (< 50 mg/dL)'
                      ].map((ci, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-3 bg-red-500/10 rounded-lg border border-red-400/30">
                          <XCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-[hsl(var(--text))]">{ci}</span>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="relatives">
                  <AccordionTrigger className="text-[hsl(var(--text))]">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-400" />
                      <span>Contre-indications relatives</span>
                      <Badge variant="outline" className="ml-2 bg-orange-500/20 border-orange-400/50">5 critères</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pt-4">
                      {[
                        'Symptômes mineurs ou en amélioration rapide',
                        'Crise convulsive au début de l\'AVC',
                        'Grossesse',
                        'Ponction lombaire récente < 7 jours',
                        'Infarctus du myocarde < 3 mois'
                      ].map((ci, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-3 bg-orange-500/10 rounded-lg border border-orange-400/30">
                          <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-[hsl(var(--text))]">{ci}</span>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <Alert className="mt-6 bg-blue-500/10 border-blue-400/30">
                <FileText className="w-4 h-4 text-blue-400" />
                <AlertDescription className="text-[hsl(var(--text))]">
                  <strong>Note importante :</strong> Ce guide est basé sur les dernières recommandations scientifiques 2024-2025. 
                  La prise en charge de l'AVC est une urgence vitale nécessitant une expertise neurologique et une imagerie cérébrale en urgence.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
